import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Instagram , Download } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { Brush } from "@/components/brand/Lettering";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { getProduct, getSotmWinner, monthLabel , shareImg } from "@/lib/api";
import { abs, breadcrumb, cadShort } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const BuyTee = ({ p }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(null);
  const [busy, setBusy] = useState(false);
  const add = async () => { setBusy(true); await addItem(p, { color: p.colors[0].name, size, qty: 1 }); setBusy(false); };
  return (
    <div className="grid gap-10 md:grid-cols-12" data-testid="winner-tee">
      <div className="md:col-span-5"><ProductCard product={p} /></div>
      <div className="md:col-span-6 md:col-start-7 md:self-center">
        <p className="label text-ink/50">Wear it</p>
        <h2 className="display mt-4 text-[clamp(2.5rem,5vw,4.5rem)]">{p.name}</h2>
        <p className="mt-3 text-lg">{cadShort(p.price)}</p>
        <div className="mt-6 grid max-w-sm grid-cols-6 gap-2">
          {Object.entries(p.sizes).map(([s, n]) => (
            <button key={s} disabled={n === 0} onClick={() => setSize(s)} aria-pressed={size === s} className={`h-11 border font-mono text-xs ${n === 0 ? "border-ink/15 text-ink/30 line-through" : size === s ? "border-ink bg-ink text-cloud" : "border-ink/40 hover:border-ink"}`} data-testid={`winner-size-${s}`}>{s}</button>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-6">
          <button onClick={add} disabled={!size || busy} className="btn btn-lime" data-testid="winner-add-to-cart">{size ? "Add to cart" : "Select a size"}</button>
          <Link to={`/product/${p.slug}`} className="link-u" data-testid="winner-product-link">Product details</Link>
        </div>
      </div>
    </div>
  );
};

export default function SotmWinner() {
  const { slug } = useParams();
  const [w, setW] = useState(undefined);
  const [product, setProduct] = useState(null);
  useEffect(() => {
    getSotmWinner(slug).then((d) => { setW(d); if (d.product_slug) getProduct(d.product_slug).then(setProduct).catch(() => {}); }).catch(() => setW(null));
  }, [slug]);

  if (w === undefined) return <Loader />;
  if (w === null) return <NotFound />;
  const ld = {
    "@context": "https://schema.org", "@type": "CreativeWork", name: w.design_title, image: abs(w.design_url), text: w.story,
    creator: { "@type": "Person", name: w.full_name, homeLocation: { "@type": "Place", name: w.city } },
    award: `Sus of the Month, ${monthLabel(w.month)}`, publisher: { "@type": "Organization", name: "the suspects" },
  };
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-10 sm:px-10" data-testid="sotm-winner-page">
      <Seo title={`${w.design_title} by ${w.first_name}: Sus of the Month ${monthLabel(w.month)}`} description={`${w.full_name}, ${w.age}, from ${w.city} won Sus of the Month with "${w.design_title}". Read the story and shop the tee.`} path={`/sus-of-the-month/${w.slug}`} image={w.design_url}
        jsonLd={[ld, breadcrumb([{ name: "Home", path: "/" }, { name: "Sus of the Month", path: "/sus-of-the-month" }, { name: w.design_title, path: `/sus-of-the-month/${w.slug}` }])]} />
      <nav aria-label="Breadcrumb" className="label text-ink/45"><Link to="/sus-of-the-month" className="hover:text-ink">Sus of the Month</Link> / <span className="text-ink">{w.design_title}</span></nav>
      <section className="mt-8 grid gap-12 lg:grid-cols-12">
        <div className="tile aspect-square overflow-hidden rounded-[24px] lg:col-span-7"><img src={w.design_url} alt={`${w.design_title}, winning tee design by ${w.full_name}`} className="h-full w-full object-cover mix-blend-multiply" data-testid="winner-design" /></div>
        <div className="flex flex-col justify-center lg:col-span-5">
          <p className="label text-ink/50">Sus of the Month · {monthLabel(w.month)}</p>
          <h1 className="display mt-4 text-[clamp(3.5rem,7vw,6.5rem)]" data-testid="winner-title">{w.design_title}</h1>
          <p className="mt-5 text-lg" data-testid="winner-creator">{w.full_name}, {w.age} · {w.city}</p>
          {w.instagram && <a href={`https://instagram.com/${w.instagram}`} target="_blank" rel="noreferrer" className="link-u mt-4 self-start" data-testid="winner-instagram"><Instagram size={14} /> @{w.instagram}</a>}
          <p className="label mt-8" data-testid="winner-votes">{w.votes} community votes</p>
          <a href={shareImg(w.id, "winner", true)} download className="btn btn-pink mt-8 self-start" data-testid="winner-story-download"><Download size={15} /> I'm Sus of the Month story</a>
        </div>
      </section>
      <section className="mt-24 grid gap-12 border-t border-ink/10 pt-16 lg:grid-cols-12" aria-labelledby="story-title">
        {w.photo_url && <div className="overflow-hidden rounded-[24px] lg:col-span-4"><img src={w.photo_url} alt={`${w.full_name}, the designer`} className="photo aspect-[4/5] w-full object-cover" data-testid="winner-photo" /></div>}
        <div className={w.photo_url ? "lg:col-span-7 lg:col-start-6" : "lg:col-span-8"}>
          <h2 id="story-title" className="label font-bold">The story</h2>
          <p className="mt-6 text-[clamp(1.25rem,2vw,1.6rem)] leading-snug" data-testid="winner-story">"{w.story}"</p>
          {w.why_note && (
            <div className="mt-12 border-l-2 border-lime pl-6" data-testid="winner-why">
              <Brush lines={["Why we", "picked it"]} rotate={-5} className="text-3xl" />
              <p className="mt-5 text-[15px] leading-relaxed text-ink/75">{w.why_note}</p>
            </div>
          )}
        </div>
      </section>
      {product && <section className="mt-24 border-t border-ink/10 pt-16">{<BuyTee p={product} />}</section>}
    </div>
  );
}
